import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CardActions,
  CircularProgress,
  Stack,
  Chip,
  IconButton,
  Alert,
  Link,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Theaters as TheaterIcon,
  Info as InfoIcon,
  Business as BusinessIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { ResponsiveCardGrid } from "@/components/ResponsiveCardGrid";
import { useStagesApi } from "@/context/AppContext";
import type { Stage } from "api-client/models";

export const StageList: React.FC = () => {
  const [stages, setStages] = useState<Stage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [infoMessage, setInfoMessage] = useState<string | null>(null);
  const navigate = useNavigate();
  const stagesApi = useStagesApi();

  useEffect(() => {
    fetchStages();
  }, []);

  const fetchStages = async () => {
    try {
      setLoading(true);
      const response = await stagesApi.stagesList();
      setStages(response.results || []);
      setError(null);
    } catch {
      setError("Failed to load stages. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (stageId: string) => {
    if (!confirm("Are you sure you want to delete this stage?")) {
      return;
    }

    try {
      await stagesApi.stagesDestroy({ id: stageId });
      fetchStages();
    } catch {
      alert("Failed to delete stage. Please try again.");
    }
  };

  if (loading) {
    return (
      <Stack alignItems="center" mt={4}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Loading stages...</Typography>
      </Stack>
    );
  }

  return (
    <Box>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", sm: "center" }}
        spacing={2}
        mb={3}
      >
        <Box>
          <Typography variant="h4" gutterBottom>
            Stages
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage stages across all your venues
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate("/stages/new")}
        >
          New Stage
        </Button>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {infoMessage && (
        <Alert
          severity="info"
          sx={{ mb: 3 }}
          onClose={() => setInfoMessage(null)}
        >
          {infoMessage}
        </Alert>
      )}

      {stages.length === 0 ? (
        <Card>
          <CardContent>
            <Stack alignItems="center" spacing={2} py={4}>
              <TheaterIcon sx={{ fontSize: 64, color: "text.secondary" }} />
              <Typography variant="h6" color="text.secondary">
                No stages yet
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Get started by creating your first stage
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => navigate("/stages/new")}
              >
                Create Stage
              </Button>
            </Stack>
          </CardContent>
        </Card>
      ) : (
        <ResponsiveCardGrid>
          {stages.map((stage) => (
            <Card
              key={stage.id}
              sx={{ display: "flex", flexDirection: "column" }}
            >
              <CardContent sx={{ flexGrow: 1 }}>
                <Stack spacing={2}>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="start"
                    spacing={1}
                  >
                    <Box
                      sx={{ minWidth: 0, flex: 1, overflowWrap: "anywhere" }}
                    >
                      <Typography variant="h6" gutterBottom>
                        {stage.name}
                      </Typography>
                      {stage.stageType && (
                        <Chip
                          label={stage.stageType}
                          size="small"
                          sx={{ mb: 1 }}
                        />
                      )}
                    </Box>
                    {stage.subscriptionStatus && (
                      <Chip
                        label={
                          stage.subscriptionStatus === "active"
                            ? "Active"
                            : "Inactive"
                        }
                        color={
                          stage.subscriptionStatus === "active"
                            ? "success"
                            : "default"
                        }
                        size="small"
                        sx={{ flexShrink: 0 }}
                      />
                    )}
                  </Stack>

                  <Stack direction="row" spacing={1} alignItems="center">
                    <BusinessIcon fontSize="small" color="action" />
                    <Link
                      component="button"
                      variant="body2"
                      onClick={() => navigate(`/venues/${stage.venue}`)}
                      sx={{
                        textDecoration: "none",
                        "&:hover": {
                          textDecoration: "underline",
                        },
                      }}
                    >
                      {stage.venueName}
                    </Link>
                  </Stack>

                  <Stack direction="row" spacing={1} alignItems="center">
                    <TheaterIcon fontSize="small" color="action" />
                    <Typography variant="body2" color="text.secondary">
                      Capacity: {stage.capacity}
                    </Typography>
                  </Stack>
                </Stack>
              </CardContent>
              <CardActions
                disableSpacing
                sx={{
                  flexWrap: "wrap",
                  gap: 1,
                  "& > button": { minHeight: 44, minWidth: 44 },
                }}
              >
                <Button
                  size="small"
                  startIcon={<InfoIcon />}
                  onClick={() => navigate(`/stages/${stage.id}`)}
                >
                  Details
                </Button>
                <Button
                  size="small"
                  startIcon={<EditIcon />}
                  onClick={() => navigate(`/stages/${stage.id}/edit`)}
                >
                  Edit
                </Button>
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => handleDelete(stage.id)}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </CardActions>
            </Card>
          ))}
        </ResponsiveCardGrid>
      )}
    </Box>
  );
};
